/**
 * Shared task-related utility functions
 *
 * This module contains common task parsing, state detection, and manipulation
 * functions used across the application to avoid code duplication.
 */

import { BlockNoteEditor } from '@packages/blocknote-core'

// Enhanced type for task state parsing with task/checklist classification
export type TaskStateInfo = {
  state: string
  bullet: string
  content: string
  isTask: boolean
  isChecklist: boolean
}

/**
 * Helper function to check if content contains a valid scheduled date pattern
 *
 * Matches >YYYY-MM-DD, >today, >tomorrow patterns but excludes >repeat(...) patterns
 * Also excludes matches when preceded by backticks (e.g., `>today`)
 */
export function hasScheduledDate(content: string): boolean {
  const scheduledDateRegex =
    /(?<=^|[^\\`])\s>(?:today|tomorrow|\d{4}-\d{2}-\d{2})\b/
  return scheduledDateRegex.test(content)
}

/**
 * Task parsing that respects BlockNoteEditor.taskItemCharacters
 * This function determines if a line is a task or checklist based on markdown settings
 *
 * @param line - The line content to parse
 * @returns Object with task/checklist classification, or undefined if not a task/checklist
 */
export function parseTaskState(line: string): TaskStateInfo | undefined {
  if (!line) return undefined

  // Check for task/checklist markers: -, +, *
  const itemMatch = line.match(/^\s*([*+-])\s+(.+)$/)
  if (!itemMatch) return undefined

  const [, bullet, fullContent] = itemMatch

  // Get task item characters from BlockNoteEditor
  const taskItemCharacters = BlockNoteEditor.taskItemCharacters

  // Check if it has bracket format - if so, it's always a task (except + which is checklist)
  const hasBrackets = /^\[[ >x-]]\s+/.test(fullContent)

  let isTask = false
  let isChecklist = false

  if (bullet === '+') {
    isChecklist = true
  } else if ((bullet === '-' || bullet === '*') && hasBrackets) {
    // Any - or * with brackets is always a task
    isTask = true
  } else {
    // For bare bullets without brackets, check taskItemCharacters
    isTask = taskItemCharacters.includes(bullet)
  }

  if (!isTask && !isChecklist) return undefined

  // Parse the state based on content
  let state: string
  let content: string

  if (fullContent.startsWith('[x] ')) {
    state = 'x'
    content = fullContent.slice(4)
  } else if (fullContent.startsWith('[-] ')) {
    state = '-'
    content = fullContent.slice(4)
  } else if (fullContent.startsWith('[>] ')) {
    state = '>'
    content = fullContent.slice(4)
  } else if (fullContent.startsWith('[ ] ')) {
    state = ' '
    content = fullContent.slice(4)
  } else {
    state = ' '
    content = fullContent
  }

  return {
    state, // Return raw state, let callers map it as needed
    bullet,
    content,
    isTask,
    isChecklist,
  }
}

/**
 * Enhanced task state toggling that supports multiple states
 *
 * @param currentState - Current task state
 * @returns New task state after toggle
 */
export function toggleTaskState(currentState: string): string {
  // Toggle logic: open/scheduled -> done, done/cancelled -> open
  switch (currentState) {
    case ' ': // Open
    case '>': {
      // Scheduled
      return 'x' // Mark as done
    }
    case 'x': // Done
    case '-': {
      // Cancelled
      return ' ' // Mark as open
    }
    default: {
      return 'x' // Default to done
    }
  }
}

/**
 * Update task line with new state
 *
 * @param line - The original line
 * @param newState - The new state to apply
 * @returns Updated line with new state
 */
export function updateTaskLineState(line: string, newState: string): string {
  if (!line) return line

  const parsed = parseTaskState(line)
  if (!parsed) return line

  const { bullet, content } = parsed
  const indent = line.match(/^(\s*)/)?.[1] ?? ''

  switch (newState) {
    case 'x': {
      return `${indent}${bullet} [x] ${content.replace(/^\s*/, '')}`
    }
    case '-': {
      return `${indent}${bullet} [-] ${content.replace(/^\s*/, '')}`
    }
    case '>': {
      // For scheduled state, preserve existing format if it contains valid scheduled date
      return hasScheduledDate(content)
        ? `${indent}${bullet} ${content}`
        : `${indent}${bullet} ${content.replace(/^\s*/, '')}`
    }
    default: {
      // For open state, remove any state markers but preserve scheduled dates
      const cleanContent = content
        .replace(/^\[.]\s*/, '') // Remove [x] or [-] markers
        .replace(/^\s*/, '') // Remove leading spaces
      return `${indent}${bullet} ${cleanContent}`
    }
  }
}

/**
 * Simple toggle function (for backward compatibility with search view)
 *
 * @param line - The line to toggle
 * @returns Line with toggled state
 */
export function toggleTodoStateInLine(line: string): string {
  const parsed = parseTaskState(line)
  if (!parsed) return line

  const newState = toggleTaskState(parsed.state)
  return updateTaskLineState(line, newState)
}
